import { useReservations } from '~/hooks/use-reservations.ts';
import type { HomeReservation } from '~/routes/_index/fetch-home-reservations.server.ts';
import { EventRowSkeleton } from './event-row-skeleton.tsx';
import { EventRow } from './event-row.tsx';
import { mapHomeEvents } from './map-home-event.ts';

type ReservationsSectionProps = {
  initialReservations: HomeReservation[];
  initialHasMore: boolean;
  selectedVenueId: string | null;
  locale?: string | null;
  timezone?: string | null;
};

const SKELETON_COUNT = 5;

export const ReservationsSection = ({
  initialReservations,
  initialHasMore,
  selectedVenueId,
  locale,
  timezone,
}: ReservationsSectionProps) => {
  const { reservations, isRefreshing, isLoadingMore, sentinelRef } = useReservations({
    initialReservations,
    initialHasMore,
    selectedVenueId,
  });

  const events = mapHomeEvents(reservations, { locale, timezone });

  return (
    <div className="flex min-h-0 flex-1 flex-col pt-12">
      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-8">
        <h2 className="mb-3 font-semibold text-ande-surface-400 text-xs uppercase tracking-[0.6px]">
          Today's Events
        </h2>

        {isRefreshing ? (
          <div className="flex flex-col gap-3">
            {Array.from({ length: SKELETON_COUNT }, (_, index) => (
              <EventRowSkeleton key={`reservation-skeleton-${index}`} />
            ))}
          </div>
        ) : events.length === 0 ? (
          <p className="py-6 text-center font-sans text-ande-gray-300 text-sm leading-5">
            No events today.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {events.map(event => (
              <EventRow key={event.id} event={event} />
            ))}
          </div>
        )}

        <div ref={sentinelRef} className="h-1" aria-hidden="true" />

        {isLoadingMore ? (
          <div className="flex justify-center py-4" aria-live="polite" aria-busy="true">
            <div
              className="size-6 animate-spin rounded-full border-2 border-ande-surface-200 border-t-ande-black"
              role="status"
              aria-label="Loading more events"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};
